import { forwardRef, useState } from 'react';

import { Box, Image, rem, Skeleton, Stack, StackProps } from '@bonusly/design-system';

import { ReactComponent as IconEmptyIncentive } from '../../assets/icon-empty-incentive.svg';

interface AvatarProps extends StackProps {
	avatar?: string;
	emoji?: string;
	name?: string;
	size?: 'lg' | 'md';
	loading?: boolean;
}

// old incentives has avatars defaulted to medal img as empty
const EMPTY_IMG = 'assets/avatar/award/medal';

const IncentiveAvatar = forwardRef(({ avatar, emoji, name = '', size, loading, ...props }: AvatarProps, ref) => {
	const [imgLoaded, setImgLoaded] = useState(false);
	const [imgError, setImgError] = useState(false);

	const isEmptyAvatar = avatar?.includes(EMPTY_IMG);
	const showAvatar = !!avatar && !imgError && !isEmptyAvatar;
	const showEmoji = !!emoji && !showAvatar; // Only show emoji as fallback when there's no valid avatar. Emoji avatar is deprecated so this case should be rare.
	const showLoading = loading || (showAvatar && !imgLoaded);
	const showEmptyIcon = (!showLoading && !showEmoji && !showAvatar) || (!showEmoji && imgError);

	// supported sizes 'lg' or 'md' only
	const sizes = {
		size: size === 'lg' ? rem(180) : rem(104),
		fontSize: size === 'lg' ? 132 : 72,
	};

	return (
		<Stack
			alignItems="center"
			aria-label={name}
			as="figure"
			bgColor={showEmptyIcon ? 'bg.subtle' : 'white'}
			justifyContent="center"
			overflow="hidden"
			position="relative"
			rounded="md"
			role="img"
			ref={ref}
			h={sizes.size}
			w={sizes.size}
			{...props}
		>
			{showLoading && <Skeleton w="100%" h="100%" position="absolute" data-testid="loading" />}

			{showAvatar && (
				<Image
					alt={name}
					fit="cover"
					src={avatar}
					h="100%"
					w="100%"
					transition="opacity 0.25s ease-in-out"
					onLoad={() => setImgLoaded(true)}
					onError={() => setImgError(true)}
					opacity={imgLoaded ? 1 : 0}
					position="absolute"
					inset={0}
				/>
			)}

			{showEmoji && <Box fontSize={sizes.fontSize}>{emoji}</Box>}

			{showEmptyIcon && <IconEmptyIncentive />}
		</Stack>
	);
});

IncentiveAvatar.displayName = 'IncentiveAvatar';

export { IncentiveAvatar };
